import axios, { AxiosResponse } from 'axios';
import { DNSValidationResponse } from '../types';
import { ENDPOINTS } from '../config/endpoints';

// Use centralized endpoint configuration
// This checks the businesses table for existing LegacyAppUrl entries
const SUBDOMAIN_CHECK_ENDPOINT = ENDPOINTS.CHECK_BUSINESS_NAME;

// Timeout for API calls (5 seconds)
// Must stay above CheckBusinessNameAvailability's 8s scan budget (this service
// calls that endpoint too). At 5s the client gave up before the server could
// answer, so any scan taking 5-8s surfaced as "couldn't check" and a genuinely
// taken subdomain was never reported as taken.
const API_TIMEOUT = 10000;

// Create axios instance with default config
const dnsApiClient = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Checks if a subdomain is available in the Azure DNS zone
 * @param subdomain - The subdomain to check (e.g., "customer123")
 * @returns Promise with DNS validation response
 */
export const checkSubdomainAvailability = async (subdomain: string): Promise<DNSValidationResponse> => {
  try {
    // Clean and validate input
    const cleanSubdomain = subdomain.trim().toLowerCase();

    console.log('=== Subdomain Availability Check ===');
    console.log('Endpoint:', SUBDOMAIN_CHECK_ENDPOINT);
    console.log('Subdomain:', cleanSubdomain);

    if (!cleanSubdomain) {
      throw new Error('Subdomain is required');
    }

    // Basic format validation (client-side)
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(cleanSubdomain)) {
      return {
        available: false,
        subdomain: cleanSubdomain,
        full_domain: `${cleanSubdomain}.app.smartr365.com`,
        error: 'Invalid subdomain format',
        message: 'Subdomain must contain only lowercase letters, numbers, and hyphens'
      };
    }

    // Check length constraints
    if (cleanSubdomain.length < 3 || cleanSubdomain.length > 63) {
      return {
        available: false,
        subdomain: cleanSubdomain,
        full_domain: `${cleanSubdomain}.app.smartr365.com`,
        error: 'Invalid subdomain length',
        message: 'Subdomain must be between 3-63 characters long'
      };
    }

    // Check for consecutive hyphens
    if (cleanSubdomain.includes('--')) {
      return {
        available: false,
        subdomain: cleanSubdomain,
        full_domain: `${cleanSubdomain}.app.smartr365.com`,
        error: 'Invalid subdomain format',
        message: 'Subdomain cannot contain consecutive hyphens'
      };
    }

    // Use business name API to check subdomain
    // The backend checks LegacyAppUrl field in businesses table
    const requestData = {
      businessName: cleanSubdomain
    };

    console.log('Making subdomain check request to:', SUBDOMAIN_CHECK_ENDPOINT);
    console.log('Request data:', requestData);

    const response: AxiosResponse<any> = await dnsApiClient.post(
      SUBDOMAIN_CHECK_ENDPOINT,
      requestData
    );

    console.log('Subdomain check response:', response.data);

    // Transform business name API response to DNS validation response format
    const fullDomain = `${cleanSubdomain}.app.smartr365.com`;
    return {
      available: response.data.available,
      status: response.data.status ?? (response.data.available ? 'available' : 'taken'),
      subdomain: cleanSubdomain,
      full_domain: fullDomain,
      message: response.data.available
        ? `${fullDomain} is available`
        : `${fullDomain} is already taken`
    };

  } catch (error) {
    console.error('DNS availability check error:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      isAxiosError: axios.isAxiosError(error),
      status: axios.isAxiosError(error) ? error.response?.status : 'N/A',
      statusText: axios.isAxiosError(error) ? error.response?.statusText : 'N/A',
      data: axios.isAxiosError(error) ? error.response?.data : 'N/A'
    });

    // As in businessNameValidation: a failure to check is not a taken name.
    // Report it as such so the caller can say so honestly and let the customer
    // proceed rather than telling them a free address is already registered.
    const clean = subdomain.trim().toLowerCase();
    const fullDomainOnError = `${clean}.app.smartr365.com`;
    const checkFailed = (message: string): DNSValidationResponse => ({
      available: false,
      status: 'check_failed',
      subdomain: clean,
      full_domain: fullDomainOnError,
      error: 'availability_check_unavailable',
      message,
    });

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        // A real axios timeout: an AxiosError with no response, which would
        // otherwise fall to `default:` and blame the customer's connection.
        return checkFailed('The address check timed out. Please try again.');
      }
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;

      switch (status) {
        case 400:
          // A genuine rejection of the input, not a failure to check it.
          return {
            available: false,
            subdomain: clean,
            full_domain: fullDomainOnError,
            error: 'Invalid request format',
            message: serverMessage || 'Please check your subdomain format',
          };

        case 409:
          return {
            available: false,
            status: 'taken',
            subdomain: clean,
            full_domain: fullDomainOnError,
            error: 'Subdomain exists',
            message: `${fullDomainOnError} is already taken`,
          };

        case 429:
          return checkFailed('Too many requests. Please try again in a moment.');

        case 503:
          return checkFailed(
            serverMessage || "We couldn't check this address right now. Please try again."
          );

        case 500:
          return checkFailed('The address check service is temporarily unavailable.');

        default:
          return checkFailed(
            "We couldn't reach the address check service. Please check your connection and try again."
          );
      }
    }

    if (error instanceof Error && error.message.includes('timeout')) {
      return checkFailed('The address check timed out. Please try again.');
    }

    return checkFailed("We couldn't check this address right now. Please try again.");
  }
};

/**
 * Debounced DNS availability checking function
 * @param subdomain - The subdomain to check
 * @param delay - Delay in milliseconds (default: 800ms)
 * @returns Promise with validation response
 */
export const debouncedCheckSubdomain = (() => {
  let timeoutId: NodeJS.Timeout;
  
  return (subdomain: string, delay: number = 800): Promise<DNSValidationResponse> => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const result = await checkSubdomainAvailability(subdomain);
        resolve(result);
      }, delay);
    });
  };
})();

/**
 * Test connection to DNS check service
 * @returns Promise indicating if the service is available
 */
export const testDNSService = async (): Promise<boolean> => {
  try {
    // Test the subdomain check service with a test subdomain
    const result = await checkSubdomainAvailability('test-subdomain-check');
    // Service is available if we got any response (available true/false doesn't matter)
    return result.available !== undefined;
  } catch (error) {
    console.warn('Subdomain service health check failed:', error);
    return false;
  }
};

const dnsValidationService = {
  checkSubdomainAvailability,
  debouncedCheckSubdomain,
  testDNSService
};

export default dnsValidationService;
